import { TONES, toneVars } from "./tones";

const darkTheme = {
  name: "dark",
  colors: {
    // ───────────────── Backgrounds ─────────────────
    "--color-bg-primary": "22 27 34", // #161B22 (main app background)
    "--color-bg-secondary": "30 36 45", // #1E242D (cards/sidebar)
    "--color-bg-tertiary": "38 45 56", // #262D38 (nested cards)
    "--color-bg-form": "30 36 45", // #1E242D (forms/inputs)
    "--color-bg-icon": "48 56 70", // #303846 (icon backgrounds)
    "--color-bg-invert": "255 255 255",

    // ───────────────── Text ─────────────────
    "--color-text-primary": "240 246 252", // #F0F6FC
    "--color-text-secondary": "170 180 190", // #8B949E
    "--color-text-tertiary": "22 27 34", // dark text on white buttons
    // #9198A1. Was #6E7681, which measured 3.02:1 on `bg-background-tertiary` —
    // well under the 4.5:1 body text needs. Tertiary is the tightest of the four
    // surfaces text sits on and now clears at 4.76:1. Still clearly dimmer than
    // `--color-text-secondary`, which is the job of this token.
    "--color-text-muted": "145 152 161", // #9198A1
    "--color-text-base": "255 255 255",
    "--color-text-blue": "56 139 253", // #388BFD
    "--color-text-invert": "0 0 0",

    // ───────────────── Borders ─────────────────
    "--color-primary-border": "48 54 61", // #30363D

    // See the note in light.js. #7C8490, 3.67:1 against the tightest surface an
    // input sits on; #30363D managed 1.03:1 and made a text field on a card
    // invisible until you clicked in it.
    "--color-input-border": "124 132 144", // #7C8490

    // ───────────────── Gradients ─────────────────
    "--color-gradient-border":
      "linear-gradient(90deg, #1E242D, #6E7681)",

    "--color-green-border":
      "linear-gradient(135deg, #2EA043, #1E242D)",

    "--color-yellow-border":
      "linear-gradient(135deg, #D29922, #1E242D)",

    "--color-pink-border":
      "linear-gradient(135deg, #C24471 0%, transparent 80%)",

    // ───────────────── Tech Stack Cards ─────────────────
    "--color-tech-stack-1":
      "linear-gradient(90deg, #1E242D, #7997C3)",

    "--color-tech-stack-2":
      "linear-gradient(90deg, #1E242D, #C4A1A1)",

    "--color-tech-stack-3":
      "linear-gradient(90deg, #1E242D, #C0A783)",

    "--color-tech-stack-4":
      "linear-gradient(90deg, #1E242D, #87AA92)",

    "--color-tech-stack-5":
      "linear-gradient(90deg, #1E242D, #A786B4)",

    "--color-tech-stack-6":
      "linear-gradient(90deg, #1E242D, #D09D81)",

    "--color-tech-stack-7":
      "linear-gradient(90deg, #1E242D, #CC8788)",

    "--color-tech-stack-8":
      "linear-gradient(90deg, #1E242D, #D474C7)",

    "--color-tech-stack-9":
      "linear-gradient(90deg, #1E242D, #6E7681)",

    // ───────────────── Buttons ─────────────────
    "--color-btn-primary": "255 255 255", // white
    // #1F6FEB. White text (--color-text-base) sits on it — every secondary
    // button's label and the outbound chat bubble — and measures 4.63:1
    // (WCAG 1.4.3). The #388BFD it replaces measured 3.3:1.
    "--color-btn-secondary": "31 111 235", // #1F6FEB
    "--color-btn-tertiary": "48 54 61", // #30363D

    // ───────────────── Sidebar ─────────────────
    "--color-sidebar-text": "139 148 158", // #8B949E
    "--color-sidebar-hover-text": "240 246 252", // #F0F6FC
    "--color-sidebar-hover-bg": "33 38 45", // #21262D
    "--color-sidebar-test": "30 36 45",

    // ───────────────── Tables ─────────────────
    "--color-table-bg": "30 36 45", // #1E242D
    "--color-table-heading": "33 38 45", // #21262D

    // ───────────────── Scrollbars ─────────────────
    "--color-scrollbar-track": "22 27 34", // #161B22
    "--color-scrollbar-thumb": "72 79 88", // subtle gray-blue
    "--color-scrollbar-thumb-hover": "110 118 129", // #6E7681

    // ───────────────── Accent ─────────────────
    "--color-accent-soft": "99 102 241",
    "--color-accent-border": "99 102 241",
    "--color-accent-text": "129 140 248",

    // ───────────────── Calendar ─────────────────
    "--color-calendar-selected-bg": "56 139 253", // #388BFD
    "--color-calendar-selected-text": "255 255 255",

    // ───────────────── Status Tones ─────────────────

    // ───────────────── Status ─────────────────
    // `text-status-error`, `bg-status-success` and the raw
    // `rgb(var(--color-error))` arbitrary values all resolve through these.
    // They were referenced across six screens while being defined nowhere:
    // `rgb(var(--color-error) / 1)` with the variable unset is an invalid
    // colour, so every one of those elements silently inherited its parent's
    // text colour instead of turning red.
    //
    // Sourced from TONES rather than picked fresh, because those eight values
    // are the ones whose contrast against their own soft fill is measured (see
    // tones.js). One hue, one pair of numbers, both themes.
    "--color-error": TONES.rose.fg.dark,
    "--color-success": TONES.emerald.fg.dark,
    "--color-warning": TONES.amber.fg.dark,
    "--color-info": TONES.blue.fg.dark,

    ...toneVars("dark"),
  },
};

export default darkTheme;